import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import _ from "lodash";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import CustomPagination from "app/widgets/Shared/Pagination/CustomPagination";
import { Search, SearchIconWrapper, StyledInputBase } from "app/shared/SearchGlobal/style";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import ControlPointOutlinedIcon from "@mui/icons-material/ControlPointOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import skuServices from "app/services/sku-services";
import { HOST } from "app/utils/constants/paths";
import { useSnackbar } from "notistack";
import SKUFilters from "./SKUFilters";
import SKUCreateModal from "./SKUCreateModal";
import SKUEditDrawer from "./SKUEditDrawer";
import SKUImportModal from "./SKUImportModal";
import SKUExportButton from "./SKUExportButton";
import ConfirmDialog from "app/shared/widgets/ConfirmDialog";

const PAGE_SIZE = 10;

const SKUList = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { authUser } = useJumboAuth();

    const [listLoading, setListLoading] = useState(false);
    const [records, setRecords] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const [searchInput, setSearchInput] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [visibility, setVisibility] = useState("visible");
    const [skuPresence, setSkuPresence] = useState("all");
    const [binPresence, setBinPresence] = useState("all");

    // Las existencias no vienen en el listado: cada código es una llamada al
    // ERP. La tabla pinta desde la base y esta columna se rellena después.
    const [stockByCode, setStockByCode] = useState({});
    const [loadingStock, setLoadingStock] = useState(false);
    const [filters, setFilters] = useState({
        filter_sku: "",
        filter_barcode: "",
        filter_retail_name: "",
        filter_retail_barcode: "",
        last_scanned_from: "",
        last_scanned_to: "",
        manual_days_min: "",
        manual_days_max: "",
    });

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [actionLoading, setActionLoading] = useState({});
    const [confirmToggle, setConfirmToggle] = useState(null);

    const profileNames = useMemo(() => authUser?.profiles?.map((profile) => profile.name) || [], [authUser]);
    const canManageModule = useMemo(() => {
        return profileNames.some((profileName) => ["superadmin", "customer_admin", "supervisor"].includes(profileName));
    }, [profileNames]);
    const canViewModule = useMemo(() => {
        return canManageModule || profileNames.includes("finanzas") || profileNames.includes("seller");
    }, [canManageModule, profileNames]);

    useEffect(() => {
        if (authUser && !canViewModule) {
            navigate("/trips");
        }
    }, [authUser, canViewModule, navigate]);

    const buildQueryParams = useCallback((withPagination = true) => {
        const params = new URLSearchParams();

        if (withPagination) {
            params.set("page", String(currentPage));
            params.set("per_page", String(PAGE_SIZE));
        }

        if (searchQuery) params.set("search", searchQuery);
        if (visibility) params.set("visibility", visibility);
        if (skuPresence) params.set("sku_presence", skuPresence);
        if (binPresence && binPresence !== "all") params.set("bin_presence", binPresence);

        Object.entries(filters).forEach(([filterName, filterValue]) => {
            if (filterValue !== undefined && filterValue !== null && String(filterValue).trim() !== "") {
                params.set(filterName, String(filterValue).trim());
            }
        });

        const queryString = params.toString();
        return queryString ? `?${queryString}` : "";
    }, [binPresence, currentPage, filters, searchQuery, skuPresence, visibility]);

    const loadSkus = useCallback(() => {
        skuServices.getSkus(buildQueryParams(true))
            .then((response) => {
                setRecords(response.data || []);
                setCurrentPage(response.meta?.current_page || 1);
                setTotalPages(response.meta?.total_pages || 1);
                setTotalCount(response.meta?.total_count || 0);
            })
            .catch((error) => {
                if (error?.response?.status === 403) {
                    navigate("/trips");
                }
            })
            .finally(() => {
                setListLoading(false);
            });
    }, [buildQueryParams, navigate]);

    useEffect(() => {
        setListLoading(true);
    }, []);

    useEffect(() => {
        if (listLoading) {
            loadSkus();
        }
    }, [listLoading, loadSkus]);

    // Existencias de la página visible, después de que la tabla ya se pintó.
    // Son 10 llamadas al ERP resueltas en el backend, así que puede demorar
    // unos segundos: la columna muestra un spinner mientras tanto en vez de
    // hacer esperar al listado completo.
    useEffect(() => {
        const codes = records.map((record) => record.sku).filter(Boolean);
        if (!codes.length) {
            setStockByCode({});
            return undefined;
        }

        let cancelled = false;
        setLoadingStock(true);

        skuServices.getStock(codes)
            .then((response) => {
                if (!cancelled) setStockByCode(response?.data || {});
            })
            .catch(() => {
                if (!cancelled) setStockByCode({});
            })
            .finally(() => {
                if (!cancelled) setLoadingStock(false);
            });

        // Si el usuario cambia de página antes de que responda el ERP, la
        // respuesta vieja no debe pisar la nueva.
        return () => {
            cancelled = true;
        };
    }, [records]);

    const debouncedSearchChange = useMemo(() => _.debounce((nextValue) => {
        setCurrentPage(1);
        setSearchQuery(nextValue);
        setListLoading(true);
    }, 450), []);

    useEffect(() => {
        return () => {
            debouncedSearchChange.cancel();
        };
    }, [debouncedSearchChange]);

    const handleSearchChange = (event) => {
        const nextValue = event.target.value;
        setSearchInput(nextValue);
        debouncedSearchChange(nextValue);
    };

    const handleFiltersChange = (nextFilters) => {
        setFilters(nextFilters);
        setCurrentPage(1);
        setListLoading(true);
    };

    const handleVisibilityChange = (nextVisibility) => {
        setVisibility(nextVisibility);
        setCurrentPage(1);
        setListLoading(true);
    };

    const handleSkuPresenceChange = (nextSkuPresence) => {
        setSkuPresence(nextSkuPresence);
        setCurrentPage(1);
        setListLoading(true);
    };

    const handleBinPresenceChange = (nextBinPresence) => {
        setBinPresence(nextBinPresence);
        setCurrentPage(1);
        setListLoading(true);
    };

    const clearFilters = () => {
        setFilters({
            filter_sku: "",
            filter_barcode: "",
            filter_retail_name: "",
            filter_retail_barcode: "",
            last_scanned_from: "",
            last_scanned_to: "",
            manual_days_min: "",
            manual_days_max: "",
        });
        setVisibility("visible");
        setSkuPresence("all");
        setBinPresence("all");
        setSearchInput("");
        setSearchQuery("");
        setCurrentPage(1);
        setListLoading(true);
    };

    const buildImageSrc = (imageUrl) => {
        if (!imageUrl) return "";
        return imageUrl.startsWith("http") ? imageUrl : `${HOST}${imageUrl}`;
    };

    const formatDateTime = (value) => {
        if (!value) return "-";
        return moment(value).format("DD/MM/YYYY HH:mm");
    };

    const toggleSkuVisibility = async (record) => {
        if (!canManageModule || !record) return;

        try {
            setActionLoading((prev) => ({ ...prev, [record.id]: true }));

            if (record.hidden) {
                await skuServices.unhideSku(record.id);
                enqueueSnackbar("SKU visible nuevamente", { variant: "success" });
            } else {
                await skuServices.hideSku(record.id);
                enqueueSnackbar("SKU ocultado correctamente", { variant: "success" });
            }

            setConfirmToggle(null);
            setListLoading(true);
        } catch (_error) {
            // Interceptor handles generic message.
        } finally {
            setActionLoading((prev) => ({ ...prev, [record.id]: false }));
        }
    };

    // La posición sale de la base junto con el resto del listado, así que se
    // pinta de inmediato. El candado indica que la fijó una persona y que la
    // sincronización del ERP no la va a sobrescribir.
    const renderBin = (record) => {
        if (!record.bin) {
            return <Typography variant="body2" color="text.secondary">-</Typography>;
        }

        return (
            <Stack direction="row" spacing={0.5} alignItems="center">
                <Chip
                    size="small"
                    label={record.bin.code}
                    color={record.bin.source === "erp" ? "info" : "default"}
                    variant="outlined"
                />
                {record.bin.locked && (
                    <Tooltip title="Fijada a mano: el ERP no la sobrescribe">
                        <LockOutlinedIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                    </Tooltip>
                )}
            </Stack>
        );
    };

    // Existencias: llegan después que la tabla porque cada una es una consulta
    // al ERP.
    const renderStock = (record) => {
        if (loadingStock) {
            return <CircularProgress size={13} />;
        }

        const entry = record.sku ? stockByCode[record.sku.toLowerCase()] : null;
        if (!entry || entry.total === null || entry.total === undefined) {
            return <Typography variant="body2" color="text.secondary">-</Typography>;
        }

        const detail = (entry.by_warehouse || [])
            .map((row) => `${row.warehouse}: ${row.stock ?? "—"}`)
            .join("\n");

        return (
            <Tooltip title={detail || ""} arrow>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {entry.total}
                </Typography>
            </Tooltip>
        );
    };

    const renderRetailCodes = (retailCodes) => {
        if (!retailCodes?.length) {
            return <Typography variant="body2" color="text.secondary">-</Typography>;
        }

        return (
            <Stack spacing={0.75}>
                {retailCodes.map((retailCode) => (
                    <Typography variant="body2" key={`${retailCode.id}-${retailCode.retail_name}-${retailCode.barcode}`}>
                        {retailCode.retail_name}: {retailCode.barcode}
                    </Typography>
                ))}
            </Stack>
        );
    };

    const renderTable = () => {
        if (listLoading) {
            return (
                <Stack alignItems="center" sx={{ py: 8 }}>
                    <CircularProgress color="warning" />
                </Stack>
            );
        }

        if (!records.length) {
            return (
                <Card>
                    <CardContent>
                        <Typography variant="body1" color="text.secondary">
                            No se encontraron SKUs con los filtros actuales.
                        </Typography>
                    </CardContent>
                </Card>
            );
        }

        return (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, width: "100%" }}>
                <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: "11%" }}>SKU</TableCell>
                            <TableCell sx={{ width: "7%" }}>Foto</TableCell>
                            <TableCell sx={{ width: "11%" }}>Código de barra</TableCell>
                            <TableCell sx={{ width: "10%" }}>Posición</TableCell>
                            <TableCell align="right" sx={{ width: "8%" }}>
                                <Tooltip title="Existencias en vivo desde el ERP. Haulers no guarda saldos.">
                                    <span>Existencias</span>
                                </Tooltip>
                            </TableCell>
                            <TableCell align="right" sx={{ width: "6%" }}>Cantidad</TableCell>
                            <TableCell sx={{ width: "13%" }}>Retail</TableCell>
                            <TableCell sx={{ width: "10%" }}>Fecha de escaneo</TableCell>
                            <TableCell sx={{ width: "10%" }}>Días desde primer pickeo manual</TableCell>
                            <TableCell sx={{ width: "10%" }}>Capturado por</TableCell>
                            <TableCell align="right" sx={{ width: "7%" }}>Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {records.map((record) => (
                            <TableRow key={record.id} hover>
                                <TableCell>{record.sku || "-"}</TableCell>
                                <TableCell>
                                    {record.image_url ? (
                                        <Tooltip title="Ver foto">
                                            <Avatar
                                                variant="rounded"
                                                src={buildImageSrc(record.image_url)}
                                                sx={{ width: 48, height: 48, cursor: "pointer" }}
                                                onClick={() => setPhotoPreview({
                                                    url: buildImageSrc(record.image_url),
                                                    sku: record.sku,
                                                })}
                                            />
                                        </Tooltip>
                                    ) : (
                                        <Avatar variant="rounded" sx={{ width: 48, height: 48 }} />
                                    )}
                                </TableCell>
                                <TableCell>{record.barcode || "-"}</TableCell>
                                <TableCell>{renderBin(record)}</TableCell>
                                <TableCell align="right">{renderStock(record)}</TableCell>
                                <TableCell align="right">{record.qty ?? "-"}</TableCell>
                                <TableCell>{renderRetailCodes(record.retail_codes)}</TableCell>
                                <TableCell>{formatDateTime(record.last_scanned_at)}</TableCell>
                                <TableCell>{record.days_since_first_manual_pick ?? "-"}</TableCell>
                                <TableCell>{record.captured_by || "-"}</TableCell>
                                <TableCell align="right">
                                    {canManageModule ? (
                                        <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                                            <Tooltip title="Editar">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => setEditRecord(record)}
                                                >
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={record.hidden ? "Oculto" : "Activo"}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color={record.hidden ? "warning" : "success"}
                                                        disabled={Boolean(actionLoading[record.id])}
                                                        onClick={() => setConfirmToggle(record)}
                                                    >
                                                        {record.hidden ? (
                                                            <VisibilityOffOutlinedIcon fontSize="small" />
                                                        ) : (
                                                            <VisibilityOutlinedIcon fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Stack>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">-</Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    if (authUser && !canViewModule) {
        return null;
    }

    return (
        <React.Fragment>
            <SKUCreateModal
                open={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onCreated={() => setListLoading(true)}
            />

            <SKUEditDrawer
                record={editRecord}
                onClose={() => setEditRecord(null)}
                onUpdated={() => setListLoading(true)}
            />

            <SKUImportModal
                open={showImportModal}
                onClose={() => setShowImportModal(false)}
                onImported={() => setListLoading(true)}
            />

            <Dialog
                open={Boolean(photoPreview)}
                onClose={() => setPhotoPreview(null)}
                maxWidth="md"
            >
                <DialogContent sx={{ p: 0, position: "relative", bgcolor: "common.black" }}>
                    <IconButton
                        onClick={() => setPhotoPreview(null)}
                        sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            color: "common.white",
                            bgcolor: "rgba(0,0,0,0.4)",
                            "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
                        }}
                    >
                        <CloseOutlinedIcon />
                    </IconButton>
                    {photoPreview && (
                        <Box
                            component="img"
                            src={photoPreview.url}
                            alt={photoPreview.sku || "Foto del SKU"}
                            sx={{
                                display: "block",
                                maxWidth: "100%",
                                maxHeight: "80vh",
                                m: "auto",
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <ConfirmDialog
                open={Boolean(confirmToggle)}
                title={confirmToggle?.hidden ? "Activar SKU" : "Desactivar SKU"}
                message={
                    <>
                        ¿Estás seguro que deseas {confirmToggle?.hidden ? 'activar' : 'desactivar'} el SKU <strong>{confirmToggle?.sku || ""}</strong>?
                        <br />
                        {confirmToggle?.hidden ? 'Volverá a estar visible.' : 'Quedará oculto.'}
                    </>
                }
                confirmText={confirmToggle?.hidden ? "Activar" : "Desactivar"}
                // Activar/desactivar es reversible desde la misma pantalla, así que el
                // botón no va en rojo: el rojo queda reservado para los borrados.
                loading={Boolean(confirmToggle && actionLoading[confirmToggle.id])}
                onClose={() => setConfirmToggle(null)}
                onConfirm={() => toggleSkuVisibility(confirmToggle)}
            />

            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Typography variant="h2">SKU sin EAN</Typography>

                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {canManageModule && (
                        <Button
                            startIcon={<UploadFileOutlinedIcon />}
                            variant="contained"
                            color="success"
                            size="small"
                            onClick={() => setShowImportModal(true)}
                        >
                            Importar Excel
                        </Button>
                    )}

                    <SKUExportButton getExportParams={() => buildQueryParams(false)} />

                    {canManageModule && (
                        <Button
                            startIcon={<ControlPointOutlinedIcon />}
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => setShowCreateModal(true)}
                        >
                            Crear
                        </Button>
                    )}

                    <Button
                        startIcon={<FilterAltOutlinedIcon />}
                        variant={showFilters ? "contained" : "outlined"}
                        size="small"
                        onClick={() => setShowFilters((prev) => !prev)}
                    >
                        Filtros
                    </Button>

                    <Search sx={{ width: 260 }}>
                        <SearchIconWrapper>
                            <SearchIcon />
                        </SearchIconWrapper>
                        <StyledInputBase
                            placeholder="Buscar SKU, códigos, retail..."
                            inputProps={{ "aria-label": "search" }}
                            sx={{ background: "white" }}
                            value={searchInput}
                            onChange={handleSearchChange}
                        />
                    </Search>
                </Stack>
            </Stack>

            <SKUFilters
                open={showFilters}
                filters={filters}
                visibility={visibility}
                skuPresence={skuPresence}
                binPresence={binPresence}
                onFiltersChange={handleFiltersChange}
                onVisibilityChange={handleVisibilityChange}
                onSkuPresenceChange={handleSkuPresenceChange}
                onBinPresenceChange={handleBinPresenceChange}
                onClearFilters={clearFilters}
            />

            {renderTable()}

            <CustomPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={PAGE_SIZE}
                onPageSelect={(page) => {
                    setCurrentPage(page);
                    setListLoading(true);
                }}
            />
        </React.Fragment>
    );
};

export default SKUList;
